import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { Link, useNavigate } from 'react-router-dom';
import { fetchInquiryInboxThreads, InquiryInboxThread, pollPlivoInquiryReplies } from './inquiryInboxApi';
import useAuthorize from '../hooks/useAuthorize';
import { INQUIRIES_MODULE_ROLES } from '../roles/portalRoles';

const InquiryInboxList = () => {
  useAuthorize(INQUIRIES_MODULE_ROLES);
  const notify = useNotify();
  const navigate = useNavigate();
  const [rows, setRows] = useState<InquiryInboxThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inquiryFilter, setInquiryFilter] = useState('');
  const [needsReply, setNeedsReply] = useState('true');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const perPage = 25;

  const load = async () => {
    setLoading(true);
    try {
      const result = await fetchInquiryInboxThreads({
        _start: page * perPage,
        _end: (page + 1) * perPage,
        q: search || undefined,
        inquiry_id: inquiryFilter || undefined,
        needs_reply: needsReply || undefined,
      } as Record<string, string | number>);
      setRows(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error(error);
      notify('Failed to load inquiry inbox', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, needsReply]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await pollPlivoInquiryReplies(7);
      const syncStats = result?.plivoSync;
      const pollStats = result?.plivoPoll;
      const synced = syncStats?.inboxUpserted ?? 0;
      const outbound = syncStats?.outboundCount ?? 0;
      const processed = pollStats?.processed ?? 0;
      notify(
        `Synced ${synced} Plivo message(s) (${outbound} outbound), processed ${processed} reply association(s)`,
        { type: 'success' }
      );
      setPage(0);
      await load();
    } catch (error) {
      console.error(error);
      notify('Failed to sync from Plivo', { type: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Inquiry Inbox (Plivo)" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Inquiry Inbox</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from Plivo'}
          </Button>
          <Button component={Link} to="/inquiries" variant="text">
            Back to Inquiries
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Seller inquiry WhatsApp on the Credco Plivo number. Sync from Plivo to import outbound replies
        and templates alongside inbound seller messages.
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search phone or message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextField
            size="small"
            label="Inquiry ID"
            value={inquiryFilter}
            onChange={(e) => setInquiryFilter(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Reply status</InputLabel>
            <Select
              label="Reply status"
              value={needsReply}
              onChange={(e) => setNeedsReply(e.target.value)}
            >
              <MenuItem value="true">Needs reply</MenuItem>
              <MenuItem value="false">Replied</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={() => { setPage(0); load(); }}>
            Apply filters
          </Button>
        </Box>
      </Paper>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Seller phone</TableCell>
              <TableCell>Inquiry</TableCell>
              <TableCell>Last message</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Reply status</TableCell>
              <TableCell>When</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No inquiry WhatsApp threads yet.</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.conversation_key}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(`/inquiry-inbox/${encodeURIComponent(row.conversation_key)}`)
                  }
                >
                  <TableCell>{row.contact_phone || '—'}</TableCell>
                  <TableCell>
                    {row.inquiry_id ? (
                      <Chip
                        size="small"
                        label={`#${row.inquiry_id}`}
                        component={Link}
                        to={`/inquiries/${row.inquiry_id}/replies`}
                        clickable
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.last_body || '—'}
                  </TableCell>
                  <TableCell>{row.last_direction || '—'}</TableCell>
                  <TableCell>
                    {row.needs_reply ? (
                      <Chip label="Needs reply" color="warning" size="small" />
                    ) : (
                      <Chip label="Replied" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {row.last_sent_at ? new Date(row.last_sent_at).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="body2">
          Showing {rows.length} of {total}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InquiryInboxList;
